import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import './Component_ResolveOrder.scss';
import { Button, ComponentSync, EditableItem, LL_H_C, LL_V_L, TS_Input } from '@nu-art/thunderstorm-frontend/index';
import { ModuleFE_Spotlight } from '../../../modules/ModuleFE_Spotlight/index.js';
import { exists, Filter, MissingDataException } from '@nu-art/ts-common';
import { ModuleFE_Advisor_Action, ModuleFE_Advisor_Variable, ModuleFE_EMInterface } from '@app/advisor-frontend';
import { FadeScrollWrapper, ICONS } from '@app/styles';
import { Renderer_DataField_Value } from '../Renderer_DataFieldV2/Renderer_DataField_Value.js';
import { Renderer_DataField_Raw } from '../Renderer_DataFieldV2/Renderer_DataField_Raw.js';
import { ModuleFE_Inbox } from '../../../modules/module-inbox/ModuleFE_Inbox.js';
import { generateOrderRawFinding } from './utils.js';
import { ModuleFE_AdvisorMini } from '../../../modules/ModuleFE_AdvisorMini.js';
import { PanelKey_Home } from '../../../_shared.js';
import { valueID_Yes } from '@app/hcs-shared';
export class Component_ResolveOrder extends ComponentSync {
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.rtOrderId = nextProps.data.rtOrderId;
        state.dbOrder = ModuleFE_Advisor_Action.cache.unique(nextProps.data.dbOrderId);
        if (state.dbOrder?.type !== 'order')
            throw new MissingDataException(`Order ID does not point to a order: ${nextProps.data.dbOrderId}`);
        state.rawFinding = this.generateRawFinding(state.dbOrder);
        return state;
    }
    //######################### Logic #########################
    normalizeProcessedRawFinding = (processedRawFinding) => {
        return {
            ...processedRawFinding,
            children: processedRawFinding.children?.filter(child => {
                return child.type === 'enumerated' ? exists(child.valueIds?.[0]) : exists(child.value);
            })
        };
    };
    generateRawFinding(dbOrder) {
        const rawFinding = generateOrderRawFinding(dbOrder);
        if (!rawFinding) {
            this.logError('Could not generate findings structure for order', dbOrder);
            return;
        }
        const editable = new EditableItem(rawFinding).setSaveAction(i => this.submitOrder(i));
        editable.setOnChanged(async () => this.forceUpdate());
        return editable;
    }
    submitOrder = async (rawFinding) => {
        const rtOrderId = this.props.data.rtOrderId;
        const processedRawFinding = this.normalizeProcessedRawFinding(rawFinding);
        try {
            await ModuleFE_EMInterface.vv1.orders.resolveOrder({
                runtimeOrderId: rtOrderId,
                processedRawFindings: [processedRawFinding],
                patientData: {
                    runtimeOrderId: rtOrderId,
                    processedRawFindings: [processedRawFinding],
                }
            }).executeSync();
            this.closeResolveOrder();
            return rawFinding;
        }
        catch (err) {
            this.logError('Failed resolving order');
            return rawFinding;
        }
    };
    closeResolveOrder = () => {
        if (ModuleFE_AdvisorMini.getIsMiniActive())
            ModuleFE_AdvisorMini.openPanel(PanelKey_Home);
        else {
            ModuleFE_Inbox.setSelectedInboxItem();
            ModuleFE_Spotlight.closeSpotlight();
        }
    };
    //######################### Render #########################
    render() {
        return _jsxs(LL_V_L, { className: 'resolve-order', children: [this.render_Header(), this.render_Body(), this.render_Footer()] });
    }
    render_Error = () => {
        return _jsx(_Fragment, { children: "Could not resolve needed findings for this order!" });
    };
    //######################### Render - Header #########################
    render_Header = () => {
        return _jsxs(LL_H_C, { className: 'resolve-order__header', children: ["Resolve Order", _jsxs(LL_H_C, { className: 'resolve-order__search', children: [_jsx(TS_Input, { type: 'text', value: this.state.searchTerm, onChange: val => this.setState({ searchTerm: val }) }), _jsx(ICONS.advisor_v5_search.component, {})] })] });
    };
    //######################### Render - Body #########################
    render_Body = () => {
        const editableFinding = this.state.rawFinding;
        if (!editableFinding)
            return this.render_Error();
        const children = editableFinding.editProp('children', []);
        const filter = new Filter(prf => [ModuleFE_Advisor_Variable.cache.unique(prf.variableId)?.name ?? '']);
        return _jsxs(LL_V_L, { className: 'resolve-order__body', children: [this.render_MainFinding(editableFinding), _jsx(FadeScrollWrapper, { scrollRef: this.props.scrollRef, children: children.item.map((item, index) => {
                        if (this.state.searchTerm && !filter.filterItem(item, this.state.searchTerm))
                            return;
                        const editable = children.editProp(index, {});
                        return this.render_ProcessedRawFinding(editable);
                    }) })] });
    };
    render_MainFinding = (editable) => {
        if (editable.item.type === 'enumerated') {
            const disabled = editable.item.valueIds?.[0] === valueID_Yes;
            return _jsx(Renderer_DataField_Value, { variableId: editable.item.variableId, containerSelector: 'resolve-order__body', maxAllowedOptions: 4, disabled: disabled, selectedValueId: editable.item.valueIds?.[0], onValueSelected: val => {
                    editable.updateObj({ valueIds: val ? [val] : undefined });
                } }, editable.item.variableId);
        }
        return _jsx(Renderer_DataField_Raw, { variableId: editable.item.variableId, containerSelector: 'resolve-order__body', value: editable.item.value, onValue: val => {
                editable.updateObj({ value: val });
            } }, editable.item.variableId);
    };
    render_ProcessedRawFinding = (processedRawFinding) => {
        if (processedRawFinding.item.type === 'enumerated')
            return _jsx(Renderer_DataField_Value, { variableId: processedRawFinding.item.variableId, containerSelector: 'resolve-order__body', maxAllowedOptions: 4, selectedValueId: processedRawFinding.item.valueIds?.[0], onValueSelected: val => {
                    processedRawFinding.updateObj({ valueIds: val ? [val] : undefined });
                } }, processedRawFinding.item.variableId);
        else if (processedRawFinding.item.type === 'numeric')
            return _jsx(Renderer_DataField_Raw, { variableId: processedRawFinding.item.variableId, containerSelector: 'resolve-order__body', value: processedRawFinding.item.value, onValue: val => {
                    processedRawFinding.updateObj({ value: val });
                } }, processedRawFinding.item.variableId);
    };
    //######################### Render - Footer #########################
    render_Footer = () => {
        return _jsxs(LL_H_C, { className: 'resolve-order__footer', children: [_jsx(Button, { variant: 'secondary', onClick: this.closeResolveOrder, children: "Cancel" }), _jsx(Button, { variant: 'primary', onClick: () => this.state.rawFinding?.save(), children: "Submit" })] });
    };
}
//# sourceMappingURL=Component_ResolveOrder.js.map